import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { polyfill as scrollPolyfill } from 'seamless-scroll-polyfill';

import ErrorPage from 'src/pages/error-page';
import Loader from 'src/components/common/loader';
import PrivateRoute from 'src/components/common/private-route';
import ScrollToTop from 'src/components/common/scroll-to-top';
import Popups from 'src/components/popups';
import PopupCookies from 'src/components/popups/popup-cookies';

import cartStore from 'src/stores/cart-store';
import globalStore from 'src/stores/global-store';

import useAmoSocialButton from 'src/hooks/use-amo-social-button';
import useGaTracker from 'src/hooks/use-ga-tracker';
import useLocale from 'src/hooks/use-locale';
import useMetaPixelCode from 'src/hooks/use-meta-pixel-code';
import useQueryParams from 'src/hooks/use-query-params';
import useSmartlookAnalytics from 'src/hooks/use-smartlook-analytics';
import useVhValue from 'src/hooks/use-vh-value';
import { AppRoute, IS_HIDE_COOKIES_POPUP } from 'src/constants';
import { CheckoutRoutes, MainPageRoutes, ResultRoutes } from 'src/routes';
import { checkDisabledCookies } from 'src/utils';

import 'src/scss/index.scss';

const App = observer(() => {
  const {
    error,
    isConfigAndThemeLoaded,
    isConfigCorrect,
    isLoading,
    isLoadedTheme,
    missingConfigVars,
    queryParams,
  } = globalStore;

  if (!isConfigAndThemeLoaded) {
    return (
      <div className="app app_err">
        {!isConfigCorrect && (
          <>
            <h2 className="app__error-header">Failed to load config params:</h2>
            <ul className="app__error-list">
              {missingConfigVars.map((item) => (
                <li key={item.name} className="app__error-list-item">
                  <span className="app__error-text">{item.name}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        {!isLoadedTheme && <h2 className="app__error-header">Failed to load theme stylesheet</h2>}
      </div>
    );
  }

  const { isCheckoutRoutesRedirect } = cartStore;
  const cookiesDisabled = checkDisabledCookies();
  const [enableCookies, setEnableCookies] = useState<boolean>(
    IS_HIDE_COOKIES_POPUP || !cookiesDisabled
  );

  useLocale();
  useVhValue();
  useGaTracker(enableCookies);
  useSmartlookAnalytics();
  useQueryParams();
  useAmoSocialButton();
  useMetaPixelCode();

  useEffect(() => {
    scrollPolyfill({ forcePolyfill: true });
  }, []);

  if (isLoading) {
    return <Loader className="loader_position_overlay" />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <div className="app">
      <ScrollToTop>
        <Routes>
          {MainPageRoutes()}
          {ResultRoutes()}
          <Route
            element={
              <PrivateRoute
                isRedirect={isCheckoutRoutesRedirect}
                redirectRoute={`${AppRoute.MAIN_PAGE_ROUTE}?${queryParams}`}
              />
            }
          >
            {CheckoutRoutes()}
          </Route>
          <Route path="*" element={<Navigate replace to={AppRoute.MAIN_PAGE_ROUTE} />} />
        </Routes>
      </ScrollToTop>
      <Popups />
      {!IS_HIDE_COOKIES_POPUP && <PopupCookies setEnableCookies={setEnableCookies} />}
    </div>
  );
});

export default App;
